import React, { ReactElement } from 'react';

import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { LegendItemProps } from '@philips-internal/oswald-ui-components';

import { ReactComponent as IssuedIcon } from '../../../../../resources/assets/arrow-right.svg';
import { ReactComponent as AdditionalAttackDoseIcon } from '../../../../../resources/assets/attack-dose.svg';
import { ReactComponent as SuspendedIcon } from '../../../../../resources/assets/block.svg';
import { ReactComponent as DismissedPendingIcon } from '../../../../../resources/assets/check-clock.svg';
import { ReactComponent as AdministeredIcon } from '../../../../../resources/assets/check.svg';
import { ReactComponent as InterruptedIcon } from '../../../../../resources/assets/circle-pause.svg';
import { ReactComponent as StartedIcon } from '../../../../../resources/assets/circle-play.svg';
import { ReactComponent as FinishIcon } from '../../../../../resources/assets/circular-arrow-check.svg';
import { ReactComponent as RestartedIcon } from '../../../../../resources/assets/circular-arrow-restarted.svg';
import { ReactComponent as SanitizedPendingIcon } from '../../../../../resources/assets/cleaningbottle-clock.svg';
import { ReactComponent as LateIcon } from '../../../../../resources/assets/clock-orange.svg';
import { ReactComponent as PendingIcon } from '../../../../../resources/assets/clock-yellow.svg';
import { ReactComponent as MedicineIcon } from '../../../../../resources/assets/medicine-block.svg';
import { ReactComponent as PatientCheck } from '../../../../../resources/assets/patient-check.svg';
import { ReactComponent as PendingRefusalIcon } from '../../../../../resources/assets/patient-clock.svg';
import { ReactComponent as CheckedFirstIcon } from '../../../../../resources/assets/patient-first.svg';
import { ReactComponent as PreparationIcon } from '../../../../../resources/assets/pill-crusher-clock.svg';
import { ReactComponent as CheckedIcon } from '../../../../../resources/assets/pill-crusher-eye.svg';

import { FoundationUtils } from '../../../../utils/foundation-utils';
import { FunctionName } from '../../../../utils/parameters';
import { ChemotherapyStatus } from '../administration-schedule/administration-schedule-types';

export const legends: LegendItemProps[] = [
  {
    color: 'signal-green-60',
    icon: <AdministeredIcon />,
    label: MessageService.translate('tws.text-legend-administered'),
    value: MessageService.translate('tws.text-legend-administered')
  },
  {
    color: 'signal-orange-45',
    icon: <CheckedFirstIcon />,
    label: MessageService.translate('button.infusion-viewer.first-check'),
    value: MessageService.translate('button.infusion-viewer.first-check')
  },
  {
    color: 'signal-green-60',
    icon: <CheckedIcon />,
    label: MessageService.translate('tws.text-legend-checked'),
    value: MessageService.translate('tws.text-legend-checked')
  },
  {
    color: 'signal-orange-45',
    icon: <DismissedPendingIcon />,
    label: MessageService.translate('tws.text-legend-dismissed-pending'),
    value: MessageService.translate('tws.text-legend-dismissed-pending')
  },
  {
    color: 'signal-green-60',
    icon: <FinishIcon />,
    label: 'tws.text-legend-terminated',
    value: 'tws.text-legend-terminated'
  },
  {
    color: 'signal-orange-45',
    icon: <InterruptedIcon />,
    label: MessageService.translate('tws.text-legend-interrupted'),
    value: MessageService.translate('tws.text-legend-interrupted')
  },
  {
    color: 'signal-green-60',
    icon: <IssuedIcon />,
    label: MessageService.translate('tws.text-legend-issued'),
    value: MessageService.translate('tws.text-legend-issued')
  },
  {
    color: 'signal-orange-45',
    icon: <LateIcon />,
    label: 'tws.text-legend-lated',
    value: 'tws.text-legend-lated'
  },
  {
    color: 'signal-green-60',
    icon: <MedicineIcon />,
    label: MessageService.translate('tws.text-legend-pending-block'),
    value: MessageService.translate('tws.text-legend-pending-block')
  },
  {
    color: 'signal-orange-45',
    icon: <PendingIcon />,
    label: MessageService.translate('tws.text-legend-pending'),
    value: MessageService.translate('tws.text-legend-pending')
  },
  {
    color: 'signal-green-60',
    icon: <PatientCheck />,
    label: MessageService.translate('tws.text-legend-administrated-refused'),
    value: MessageService.translate('tws.text-legend-administrated-refused')
  },
  {
    color: 'signal-orange-45',
    icon: <PendingRefusalIcon />,
    label: MessageService.translate('twx.text-legend-pending-refuse'),
    value: MessageService.translate('twx.text-legend-pending-refuse')
  },
  {
    color: 'signal-green-60',
    icon: <PreparationIcon />,
    label: MessageService.translate('tws.text-legend-pending-prepared'),
    value: MessageService.translate('tws.text-legend-pending-prepared')
  },
  {
    color: 'signal-orange-45',
    icon: <RestartedIcon />,
    label: MessageService.translate('tws.text-legend-restarted'),
    value: MessageService.translate('tws.text-legend-restarted')
  },
  {
    color: 'signal-green-60',
    icon: <SanitizedPendingIcon />,
    label: MessageService.translate('tws.text-legend-sanitized-pending'),
    value: MessageService.translate('tws.text-legend-sanitized-pending')
  },
  {
    color: 'signal-orange-45',
    icon: <StartedIcon />,
    label: MessageService.translate('tws.text-legend-started'),
    value: MessageService.translate('tws.text-legend-started')
  },
  {
    color: 'signal-green-60',
    icon: <SuspendedIcon />,
    label: MessageService.translate('tws.text-legend-suspended'),
    value: MessageService.translate('tws.text-legend-suspended')
  }, {
    color: 'signal-yellow-60',
    icon: <AdditionalAttackDoseIcon />,
    label: MessageService.translate('tws.additional-attack-dose'),
    value: MessageService.translate('tws.additional-attack-dose'),
  }
]

export function getStatusIcon(status: string, itemType: string, delayed: boolean, reasonForsettlementCode?: number): ReactElement | undefined {
  let showIconMedicineClock = false;

  if (reasonForsettlementCode && reasonForsettlementCode > 0) {
    const useLegendDownloadedItemParam = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, 'USE_LEGEND_DOWNLOADED_ITEMS_IN_THE_MNGT_TAB#310');
    if (useLegendDownloadedItemParam) {
      showIconMedicineClock = Number(useLegendDownloadedItemParam) > 0 && useLegendDownloadedItemParam === 'S';
    }
  }

  const medicineClockCases = [
    'STARTED',
    'ADMINISTERED',
    'INTERRUPTED',
    'SUSPENDED',
    'CHECKED_FIRST',
    'PRESCRIPTION_II',
    'PRESCRIPTION_DT',
    'PRESCRIPTION_DI'
  ];

  if (showIconMedicineClock && medicineClockCases.includes(status)) {
    return <MedicineIcon />;
  }

  switch (status) {
    case 'DISMISSED_PENDING': return <DismissedPendingIcon />;
    case 'SANITIZED_PENDING': return <SanitizedPendingIcon />;
    case 'PREPARATION': return itemType !== 'O' ? <PreparationIcon /> : undefined;
    case 'STARTED': return <StartedIcon />;
    case 'RESTARTED': return <RestartedIcon />;
    case 'ADMINISTERED': return <AdministeredIcon />;
    case 'INTERRUPTED': return <InterruptedIcon />;
    case 'SUSPENDED': return <SuspendedIcon />;
    case 'ADDITIONAL_ATTACK_DOSE': return <AdditionalAttackDoseIcon />;
    case 'ISSUED': return <IssuedIcon />;
    case 'CHECKED': return <CheckedIcon />;
    case 'CHECKED_FIRST': return <CheckedFirstIcon />;
    case 'PENDING_REFUSAL': return <PendingRefusalIcon />;
    case 'PENDING': return isDelayed(delayed);
    case 'REFUSED_ADMINISTERED': return <PatientCheck />;
    default: return undefined;
  }
}

function isDelayed(delayed: boolean): ReactElement {
  return delayed ? <LateIcon /> : <PendingIcon />
}

export function getItemStatusIcon(itemStatus: string, delayed: boolean): ReactElement | null {
  switch (itemStatus) {
    case 'I': return <StartedIcon />;
    case 'II': return <InterruptedIcon />;
    case 'INT': return <InterruptedIcon />;
    case 'IN': return <InterruptedIcon />;
    case 'T': return <FinishIcon />;
    case 'TE': return <FinishIcon />;
    case 'R': return <RestartedIcon />;
    case 'A': return <LateIcon />;
    case 'N': return isDelayed(delayed);
    case 'P': return isDelayed(delayed);
    default: return null;
  }
}

export function getChemotherapyStatusIcon(chemotherapyStatus: string, delayed: boolean): ReactElement {
  switch (chemotherapyStatus) {
    case ChemotherapyStatus.PENDING: return isDelayed(delayed);
    case ChemotherapyStatus.STARTED: return <StartedIcon />;
    case ChemotherapyStatus.ADMINISTERED: return <AdministeredIcon />;
    case 'PENDING_REFUSAL': return <PendingRefusalIcon />;
    case ChemotherapyStatus.SUSPENDED: return <SuspendedIcon />;
    default: return isDelayed(delayed);
  }
}
